/**
 * SpriteFactory — generates all game textures programmatically.
 *
 * Call `generateAll(scene)` once during boot/preload to create
 * textures that can be used anywhere via `scene.add.image(x, y, key)`.
 *
 * Textures are drawn at 1x pixel scale and displayed with setScale()
 * for a crisp pixel art look.
 */

declare const Phaser: any;

// --- Helpers ---

function gfx(scene: any): any {
  return scene.make.graphics({ x: 0, y: 0, add: false });
}

function pixel(g: any, x: number, y: number, color: number): void {
  g.fillStyle(color);
  g.fillRect(x, y, 1, 1);
}

function rect(g: any, x: number, y: number, w: number, h: number, color: number): void {
  g.fillStyle(color);
  g.fillRect(x, y, w, h);
}

// --- Player character (16x16 pixel art) ---

function generatePlayer(scene: any): void {
  const g = gfx(scene);
  const skin = 0xf5c6a0;
  const hair = 0x5b3a1a;
  const shirt = 0x3b82f6;
  const shirtDark = 0x2563eb;
  const pants = 0x1e3a5f;
  const boots = 0x4b3621;

  // Hair
  rect(g, 6, 1, 4, 2, hair);
  rect(g, 5, 2, 6, 1, hair);

  // Head
  rect(g, 6, 3, 4, 4, skin);
  // Eyes
  pixel(g, 7, 4, 0x1e293b);
  pixel(g, 9, 4, 0x1e293b);
  // Mouth
  pixel(g, 8, 6, 0xd4956a);

  // Neck
  pixel(g, 7, 7, skin);
  pixel(g, 8, 7, skin);

  // Shirt body
  rect(g, 5, 8, 6, 4, shirt);
  rect(g, 6, 8, 4, 4, shirtDark);
  // Shirt detail — collar
  pixel(g, 7, 8, 0xdbeafe);
  pixel(g, 8, 8, 0xdbeafe);

  // Arms
  rect(g, 4, 8, 1, 3, shirt);
  rect(g, 11, 8, 1, 3, shirt);
  pixel(g, 4, 11, skin);
  pixel(g, 11, 11, skin);

  // Belt
  rect(g, 5, 12, 6, 1, 0x78350f);
  pixel(g, 8, 12, 0xfbbf24); // buckle

  // Pants
  rect(g, 6, 13, 2, 2, pants);
  rect(g, 9, 13, 2, 2, pants);

  // Boots
  rect(g, 5, 15, 3, 1, boots);
  rect(g, 9, 15, 3, 1, boots);

  g.generateTexture("player", 16, 16);
  g.destroy();
}

// --- Enemy: Slime (16x16) ---

function generateSlime(scene: any): void {
  const g = gfx(scene);
  const body = 0x22c55e;
  const dark = 0x16a34a;
  const light = 0x4ade80;

  // Body blob
  rect(g, 4, 8, 8, 6, body);
  rect(g, 3, 9, 10, 4, body);
  rect(g, 2, 10, 12, 3, body);
  rect(g, 3, 13, 10, 2, body);
  rect(g, 5, 14, 6, 1, dark);

  // Top roundness
  rect(g, 5, 7, 6, 1, body);
  rect(g, 6, 6, 4, 1, body);

  // Highlight
  rect(g, 5, 8, 2, 2, light);

  // Eyes
  rect(g, 5, 10, 2, 2, 0xffffff);
  rect(g, 9, 10, 2, 2, 0xffffff);
  pixel(g, 6, 11, 0x1e293b);
  pixel(g, 10, 11, 0x1e293b);

  // Mouth
  rect(g, 7, 12, 2, 1, dark);

  g.generateTexture("enemy_slime", 16, 16);
  g.destroy();
}

// --- Enemy: Goblin Chief (16x16) ---

function generateGoblinChief(scene: any): void {
  const g = gfx(scene);
  const skin = 0x6b8e23;
  const skinDark = 0x556b2f;
  const armor = 0x7c3aed;
  const armorDark = 0x6d28d9;

  // Ears
  pixel(g, 3, 4, skin);
  pixel(g, 12, 4, skin);

  // Head
  rect(g, 5, 2, 6, 5, skin);
  rect(g, 6, 1, 4, 1, skinDark); // brow

  // Crown/helmet
  rect(g, 5, 1, 6, 1, 0xfbbf24);
  pixel(g, 6, 0, 0xfbbf24);
  pixel(g, 8, 0, 0xfbbf24);
  pixel(g, 10, 0, 0xfbbf24);

  // Eyes — angry
  rect(g, 6, 3, 2, 2, 0xffffff);
  rect(g, 9, 3, 2, 2, 0xffffff);
  pixel(g, 7, 4, 0xdc2626);
  pixel(g, 9, 4, 0xdc2626);

  // Mouth — fangs
  rect(g, 7, 6, 3, 1, skinDark);
  pixel(g, 7, 6, 0xffffff);
  pixel(g, 9, 6, 0xffffff);

  // Armor body
  rect(g, 5, 8, 6, 4, armor);
  rect(g, 6, 8, 4, 4, armorDark);
  // Shoulder pads
  rect(g, 4, 8, 1, 2, armor);
  rect(g, 11, 8, 1, 2, armor);

  // Arms
  pixel(g, 3, 9, skin);
  pixel(g, 3, 10, skin);
  pixel(g, 12, 9, skin);
  pixel(g, 12, 10, skin);

  // Legs
  rect(g, 6, 12, 2, 3, skinDark);
  rect(g, 9, 12, 2, 3, skinDark);

  // Boots
  rect(g, 5, 14, 3, 1, 0x4b3621);
  rect(g, 9, 14, 3, 1, 0x4b3621);

  g.generateTexture("enemy_goblin", 16, 16);
  g.destroy();
}

// --- Enemy: Dragon Boss (20x20) ---

function generateDragon(scene: any): void {
  const g = gfx(scene);
  const body = 0x991b1b;
  const belly = 0xfbbf24;
  const dark = 0x7f1d1d;
  const wing = 0xdc2626;

  // Wings (behind body)
  rect(g, 1, 5, 4, 6, wing);
  rect(g, 15, 5, 4, 6, wing);
  rect(g, 0, 4, 3, 2, wing);
  rect(g, 17, 4, 3, 2, wing);

  // Body
  rect(g, 6, 6, 8, 10, body);
  rect(g, 7, 5, 6, 1, body);

  // Belly
  rect(g, 8, 9, 4, 5, belly);

  // Head
  rect(g, 7, 2, 6, 4, body);
  rect(g, 8, 1, 4, 1, body);

  // Horns
  pixel(g, 7, 0, dark);
  pixel(g, 12, 0, dark);
  pixel(g, 7, 1, dark);
  pixel(g, 12, 1, dark);

  // Eyes — glowing
  rect(g, 8, 3, 2, 1, 0xfbbf24);
  rect(g, 11, 3, 2, 1, 0xfbbf24);
  pixel(g, 9, 3, 0xef4444);
  pixel(g, 11, 3, 0xef4444);

  // Snout
  rect(g, 9, 5, 3, 1, dark);
  pixel(g, 9, 5, 0xff6b00); // fire nostril
  pixel(g, 11, 5, 0xff6b00);

  // Legs
  rect(g, 7, 16, 2, 3, dark);
  rect(g, 11, 16, 2, 3, dark);

  // Claws
  pixel(g, 7, 19, 0x333333);
  pixel(g, 8, 19, 0x333333);
  pixel(g, 11, 19, 0x333333);
  pixel(g, 12, 19, 0x333333);

  // Tail
  rect(g, 14, 14, 3, 2, body);
  rect(g, 16, 13, 2, 2, dark);

  g.generateTexture("enemy_dragon", 20, 20);
  g.destroy();
}

// --- Map icons (10x10 each) ---

function generateMapIcons(scene: any): void {
  // Sword icon (battle)
  let g = gfx(scene);
  rect(g, 4, 1, 2, 6, 0xd1d5db); // blade
  rect(g, 5, 1, 1, 1, 0xffffff); // shine
  rect(g, 2, 7, 6, 1, 0x78350f); // guard
  rect(g, 4, 8, 2, 2, 0x92400e); // hilt
  g.generateTexture("icon_battle", 10, 10);
  g.destroy();

  // Star icon (elite)
  g = gfx(scene);
  rect(g, 4, 1, 2, 2, 0xc084fc);
  rect(g, 2, 3, 6, 2, 0xa855f7);
  rect(g, 3, 5, 4, 1, 0xa855f7);
  rect(g, 3, 5, 1, 2, 0xc084fc);
  rect(g, 6, 5, 1, 2, 0xc084fc);
  rect(g, 4, 7, 2, 1, 0xa855f7);
  g.generateTexture("icon_elite", 10, 10);
  g.destroy();

  // Coin icon (shop)
  g = gfx(scene);
  rect(g, 3, 2, 4, 6, 0xeab308);
  rect(g, 2, 3, 6, 4, 0xfbbf24);
  rect(g, 4, 3, 2, 4, 0xeab308); // center line
  pixel(g, 3, 3, 0xfde68a); // shine
  g.generateTexture("icon_shop", 10, 10);
  g.destroy();

  // Chest icon (treasure)
  g = gfx(scene);
  rect(g, 2, 4, 6, 4, 0x92400e);
  rect(g, 1, 4, 8, 1, 0x78350f); // lid edge
  rect(g, 2, 2, 6, 2, 0xb45309); // lid
  rect(g, 4, 5, 2, 2, 0xfbbf24); // lock
  g.generateTexture("icon_treasure", 10, 10);
  g.destroy();

  // Heart icon (rest)
  g = gfx(scene);
  rect(g, 2, 3, 2, 2, 0x22c55e);
  rect(g, 6, 3, 2, 2, 0x22c55e);
  rect(g, 1, 4, 8, 2, 0x22c55e);
  rect(g, 2, 6, 6, 1, 0x16a34a);
  rect(g, 3, 7, 4, 1, 0x16a34a);
  rect(g, 4, 8, 2, 1, 0x16a34a);
  g.generateTexture("icon_rest", 10, 10);
  g.destroy();

  // Question icon (event)
  g = gfx(scene);
  rect(g, 3, 2, 4, 1, 0x818cf8);
  rect(g, 6, 3, 1, 2, 0x818cf8);
  rect(g, 4, 5, 2, 1, 0x6366f1);
  rect(g, 4, 6, 1, 1, 0x6366f1);
  rect(g, 4, 8, 1, 1, 0x6366f1);
  g.generateTexture("icon_event", 10, 10);
  g.destroy();

  // Skull icon (boss)
  g = gfx(scene);
  rect(g, 3, 1, 4, 3, 0xf5f5f4);
  rect(g, 2, 2, 6, 3, 0xf5f5f4);
  rect(g, 3, 5, 4, 2, 0xe7e5e4);
  // Eye sockets
  rect(g, 3, 3, 2, 2, 0x1e293b);
  rect(g, 6, 3, 2, 2, 0x1e293b);
  // Nose
  pixel(g, 5, 5, 0x78716c);
  // Teeth
  pixel(g, 3, 7, 0xf5f5f4);
  pixel(g, 5, 7, 0xf5f5f4);
  pixel(g, 7, 7, 0xf5f5f4);
  g.generateTexture("icon_boss", 10, 10);
  g.destroy();
}

// --- Player map avatar (10x10) ---

function generatePlayerMapAvatar(scene: any): void {
  const g = gfx(scene);
  const skin = 0xf5c6a0;
  const shirt = 0x3b82f6;

  // Head
  rect(g, 3, 1, 4, 3, skin);
  pixel(g, 4, 2, 0x1e293b); // eye
  pixel(g, 6, 2, 0x1e293b); // eye

  // Hair
  rect(g, 3, 0, 4, 1, 0x5b3a1a);

  // Body
  rect(g, 3, 4, 4, 3, shirt);

  // Arms
  pixel(g, 2, 5, shirt);
  pixel(g, 7, 5, shirt);

  // Legs
  rect(g, 3, 7, 2, 2, 0x1e3a5f);
  rect(g, 5, 7, 2, 2, 0x1e3a5f);

  // Boots
  pixel(g, 3, 9, 0x4b3621);
  pixel(g, 4, 9, 0x4b3621);
  pixel(g, 5, 9, 0x4b3621);
  pixel(g, 6, 9, 0x4b3621);

  g.generateTexture("player_map", 10, 10);
  g.destroy();
}

// --- Public API ---

export function generateAllSprites(scene: any): void {
  generatePlayer(scene);
  generateSlime(scene);
  generateGoblinChief(scene);
  generateDragon(scene);
  generateMapIcons(scene);
  generatePlayerMapAvatar(scene);
}
